/*
 * Pet-food PDP interactions.
 *
 * Self-contained on purpose: the theme's VariantSelects re-fetches the whole
 * section and expects ids (price-{id}, Inventory-, Volume-, Sku-) that this
 * custom markup does not render, so reusing it would throw. Add-to-cart still
 * goes through the theme's product-form.js, which owns the cart drawer.
 *
 * Purchase options (one-time vs subscribe) are owned by the Subify app, which
 * injects its own widget into the form and manages the selling_plan field. This
 * script deliberately never touches selling_plan - doing so silently downgraded
 * subscriptions to one-time purchases.
 */
(function () {
  'use strict';

  var money = function (cents, sample) {
    // Mirror the shop's format by reusing a server-rendered sample string.
    var value = (cents / 100).toFixed(2);
    if (!sample) return '$' + value;
    return sample.replace(/[\d.,]+/, function () {
      return Number(value).toLocaleString(undefined, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      });
    });
  };

  if (!customElements.get('pf-variant-picker')) {
    customElements.define(
      'pf-variant-picker',
      class PfVariantPicker extends HTMLElement {
        connectedCallback() {
          var json = this.querySelector('[data-pf-variants]');
          if (!json) return;
          try {
            this.variants = JSON.parse(json.textContent);
          } catch (e) {
            return;
          }
          this.addEventListener('change', this.onChange.bind(this));
          this.refreshAvailability();
          this.refreshSelection();
        }

        get chosen() {
          return Array.prototype.map.call(this.querySelectorAll('fieldset'), function (fs) {
            var picked = fs.querySelector('input:checked');
            return picked ? picked.value : null;
          });
        }

        match(options) {
          return this.variants.find(function (v) {
            return v.options.every(function (o, i) {
              return o === options[i];
            });
          });
        }

        onChange() {
          var variant = this.match(this.chosen);
          this.refreshAvailability();
          this.refreshSelection();
          this.dispatchEvent(
            new CustomEvent('pf:variant', { bubbles: true, detail: { variant: variant } })
          );
        }

        /*
         * Echo the chosen value next to each group label, and repoint every
         * photo swatch at the variant that matches the shopper's other choices.
         * Picking a protein therefore updates the size swatches to that same
         * protein instead of leaving a stale flavour on screen.
         */
        refreshSelection() {
          var self = this;
          var chosen = this.chosen;

          Array.prototype.forEach.call(this.querySelectorAll('[data-pf-opt-selected]'), function (el) {
            var pos = parseInt(el.getAttribute('data-pf-opt-selected'), 10);
            el.textContent = chosen[pos - 1] || '';
          });

          Array.prototype.forEach.call(this.querySelectorAll('[data-pf-swatch]'), function (img) {
            var pos = parseInt(img.getAttribute('data-opt-pos'), 10);
            var value = img.getAttribute('data-opt-value');
            var probe = chosen.slice();
            probe[pos - 1] = value;

            var exact = self.variants.find(function (v) {
              return v.options.every(function (o, i) {
                return o === probe[i];
              });
            });
            /* That combination may not exist - fall back to any variant
               carrying this value so the pill always shows something real. */
            var best =
              exact ||
              self.variants.find(function (v) {
                return v.options[pos - 1] === value;
              });

            var src = best && (best.swatch || best.image);
            if (src && img.getAttribute('src') !== src) img.setAttribute('src', src);
          });
        }

        /* Grey out option values that cannot combine with the current picks. */
        refreshAvailability() {
          var self = this;
          var chosen = this.chosen;
          Array.prototype.forEach.call(this.querySelectorAll('fieldset'), function (fs, index) {
            Array.prototype.forEach.call(fs.querySelectorAll('input'), function (input) {
              var probe = chosen.slice();
              probe[index] = input.value;
              var hit = self.variants.find(function (v) {
                return v.options.every(function (o, i) {
                  return i === index ? o === probe[i] : chosen[i] == null || o === chosen[i];
                });
              });
              var label = fs.querySelector('label[for="' + input.id + '"]');
              if (!label) return;
              label.classList.toggle('pf-opt__pill--soldout', !(hit && hit.available));
            });
          });
        }
      }
    );
  }

  var initRoot = function (root) {
    if (root.dataset.pfReady === 'true') return;
    root.dataset.pfReady = 'true';

    var picker = root.querySelector('[data-pf-picker]');
    var idInput = root.querySelector('[data-pf-variant-id]');
    var qtyInput = root.querySelector('[data-pf-qty-input]');
    var atc = root.querySelector('[data-pf-atc]');
    var atcTotal = root.querySelector('[data-pf-atc-total]');
    var atcSep = root.querySelector('[data-pf-atc-sep]');
    var atcText = root.querySelector('[data-pf-atc-text]');
    var oneTimePrice = root.querySelector('[data-pf-onetime-price]');
    var priceWas = root.querySelector('[data-pf-price-was]');
    var priceSave = root.querySelector('[data-pf-price-save]');
    var mainImage = root.querySelector('[data-pf-main-image]');
    var form = root.querySelector('form');

    var sticky = root.querySelector('[data-pf-sticky]');
    var stickyImg = root.querySelector('[data-pf-sticky-img]');
    var stickyPrice = root.querySelector('[data-pf-sticky-price]');
    var stickyWas = root.querySelector('[data-pf-sticky-was]');
    var stickyBtn = root.querySelector('[data-pf-sticky-add]');
    var stickyLabel = root.querySelector('[data-pf-sticky-label]');
    var stickySelect = root.querySelector('[data-pf-sticky-select]');
    var stickyAddLabel = stickyLabel ? stickyLabel.textContent.trim() : 'Add to cart';

    var priceSample = (oneTimePrice && oneTimePrice.textContent.trim()) ||
      (atcTotal && atcTotal.textContent.trim()) || '';
    var soldOutLabel = (atc && atc.getAttribute('data-soldout-label')) || 'Sold out';

    var state = { variant: null };
    var lastVariantId;
    if (picker && picker.variants) {
      state.variant = picker.match(picker.chosen) || null;
    } else {
      /* No options means no picker, and the picker is what published the
         variant data - so state.variant stayed null for the life of the page.
         render() cannot tell that apart from an unavailable variant, so every
         single-variant product came out with Add to cart disabled and
         relabelled sold out. The section emits the one variant on its own. */
      var solo = root.querySelector('[data-pf-variant-single]');
      if (solo) {
        try {
          state.variant = JSON.parse(solo.textContent);
        } catch (e) {
          /* Leave it null: the server already rendered the correct button. */
        }
      }
    }

    var qty = function () {
      var n = parseInt(qtyInput && qtyInput.value, 10);
      return isNaN(n) || n < 1 ? 1 : n;
    };

    /*
     * When the subscription app is on the page it owns pricing (its widget shows
     * the one-time and subscribe prices). Showing our own total next to it would
     * contradict whichever option the shopper picked, so the total is dropped.
     */
    /*
     * Decided server-side from the product's selling plans, not by looking for
     * the app's markup. Sniffing for a Subify container hid our total on gear
     * pages where the app renders an upsell but no price - leaving the page
     * with no price at all - and the container arrives asynchronously, so the
     * answer changed between first paint and the next variant change.
     */
    var appOwnsPricing = function () {
      return root.dataset.pfAppPricing === 'true';
    };

    var render = function () {
      var v = state.variant;

      if (idInput && v) idInput.value = v.id;

      if (v) {
        if (oneTimePrice) oneTimePrice.textContent = money(v.price, priceSample);

        /* Compare-at and the saving badge follow the variant too: without this
           a sale price from one size would sit next to another size's price. */
        var wasPrice = v.compare_at_price && v.compare_at_price > v.price
          ? v.compare_at_price : 0;
        if (priceWas) {
          priceWas.hidden = !wasPrice;
          if (wasPrice) priceWas.textContent = money(wasPrice, priceSample);
        }
        if (priceSave) {
          priceSave.hidden = !wasPrice;
          if (wasPrice) {
            priceSave.textContent = Math.round((wasPrice - v.price) / wasPrice * 100) + '% off';
          }
        }
        /* Show the variant's own photo - but only when the variant actually
           changes. render() also runs on quantity changes and again once the
           apps have loaded, and snapping the gallery back on each of those
           threw away whichever photo the shopper had moved on to. */
        if (v.id !== lastVariantId) {
          lastVariantId = v.id;
          if (v.image && typeof showMedia === 'function') {
            var hit = mediaList.findIndex(function (m) { return m.full === v.image; });
            if (hit > -1 && hit !== mediaIndex) {
              showMedia(hit, { smooth: false });
            } else if (hit === -1 && mainImage) {
              mainImage.src = v.image;
              mainImage.removeAttribute('srcset');
            }
          }
        }

        /* Ingredients copy is authored per variant, so swap which block shows.
           All of them are in the DOM already - no round trip needed. */
        Array.prototype.forEach.call(root.querySelectorAll('[data-pf-ing-for]'), function (el) {
          el.hidden = String(el.getAttribute('data-pf-ing-for')) !== String(v.id);
        });
      }

      /* Only ever contradict the server when there is a picker: then a null
         variant means the chosen combination does not exist and the button
         really should be dead. With nothing to choose, the server already
         rendered the right button and a variant we failed to read is no
         reason to disable it. */
      if (atc && (v || picker)) {
        var available = !!(v && v.available);
        atc.disabled = !available;

        if (!available) {
          if (atcText) atcText.textContent = soldOutLabel;
          return;
        }

        if (appOwnsPricing()) {
          if (atcTotal) atcTotal.hidden = true;
          if (atcSep) atcSep.hidden = true;
        } else if (atcTotal) {
          atcTotal.hidden = false;
          if (atcSep) atcSep.hidden = false;
          atcTotal.textContent = money(v.price * qty(), priceSample);
        }
      }

      renderSticky();
    };

    /*
     * The sticky bar mirrors whatever price the shopper can actually see. When
     * the subscription app is present it owns pricing, so its selected choice is
     * echoed rather than recomputed - that way the pill can never contradict the
     * widget a few hundred pixels above it.
     */
    var renderSticky = function () {
      if (!sticky) return;
      var v = state.variant;

      var appChoice = root.querySelector(
        '.subify-choice-box-wrapper.active .subify-choice-price'
      );
      var shown = appChoice
        ? appChoice.textContent.trim()
        : v
        ? money(v.price, priceSample)
        : '';

      /* Assign only on real change: a MutationObserver watches this subtree, so
         unconditional writes would retrigger it in a loop. */
      if (stickyPrice && stickyPrice.textContent.trim() !== shown) {
        stickyPrice.textContent = shown;
      }

      if (stickyWas) {
        var showWas = !appChoice && v && v.compare_at_price > v.price;
        if (stickyWas.hidden !== !showWas) stickyWas.hidden = !showWas;
        if (showWas) {
          var wasText = money(v.compare_at_price, priceSample);
          if (stickyWas.textContent.trim() !== wasText) stickyWas.textContent = wasText;
        }
      }

      if (stickyImg && v && v.image && stickyImg.getAttribute('src') !== v.image) {
        stickyImg.src = v.image;
      }

      if (stickySelect && v && String(stickySelect.value) !== String(v.id)) {
        stickySelect.value = String(v.id);
      }

      if (stickyBtn && (v || picker)) {
        var ok = !!(v && v.available);
        if (stickyBtn.disabled !== !ok) stickyBtn.disabled = !ok;
        var wanted = ok ? stickyAddLabel : soldOutLabel;
        if (stickyLabel && stickyLabel.textContent.trim() !== wanted) {
          stickyLabel.textContent = wanted;
        }
      }
    };

    /*
     * The subscription app rewrites its prices asynchronously after a variant
     * change, so the bar cannot just mirror once and be done - switching 12oz
     * to 36oz left it showing the old figure. Watch the buy box and re-mirror
     * whenever the app actually changes something.
     */
    if (sticky && typeof MutationObserver !== 'undefined') {
      var watched = root.querySelector('.pf-buybox') || root;
      var pending = null;
      new MutationObserver(function () {
        if (pending) clearTimeout(pending);
        pending = setTimeout(renderSticky, 120);
      }).observe(watched, { childList: true, subtree: true, characterData: true });
    }

    var setSticky = function (show) {
      if (!sticky) return;
      sticky.setAttribute('data-visible', show ? 'true' : 'false');
      sticky.setAttribute('aria-hidden', show ? 'false' : 'true');
      /* Keep it out of the tab order while it is off screen. */
      if (stickyBtn) stickyBtn.tabIndex = show ? 0 : -1;
    };

    /*
     * Reveal once the real buy box has scrolled off the top. Driven by scroll
     * position rather than IntersectionObserver: one code path, no reliance on
     * observer callbacks firing, and trivial to reason about.
     */
    var syncSticky = function () {
      if (!sticky || !atc) return;
      setSticky(atc.getBoundingClientRect().bottom < 0);
    };

    if (sticky && atc) {
      /* Called straight from the listener rather than through a rAF throttle:
         it is a single geometry read on a passive listener, and going through
         rAF meant nothing updated wherever those callbacks are paused. */
      window.addEventListener('scroll', syncSticky, { passive: true });
      window.addEventListener('resize', syncSticky);
      syncSticky();
    }

    /*
     * The bar's selector does not set the variant itself - it ticks the matching
     * radios and lets the picker fire its usual change. One source of truth, so
     * price, image, ingredients and the buy box all follow the same path they do
     * when someone uses the pills directly.
     */
    if (stickySelect && picker && picker.variants) {
      stickySelect.addEventListener('change', function () {
        var chosen = picker.variants.find(function (candidate) {
          return String(candidate.id) === String(stickySelect.value);
        });
        if (!chosen) return;

        Array.prototype.forEach.call(picker.querySelectorAll('fieldset'), function (fs, i) {
          var want = chosen.options[i];
          /* Compare values rather than building an attribute selector: option
             values contain quotes (EXTRA SMALL: 11"-14"), which would break one. */
          Array.prototype.forEach.call(fs.querySelectorAll('input'), function (input) {
            if (input.value === want) input.checked = true;
          });
        });

        picker.dispatchEvent(new Event('change', { bubbles: true }));
      });
    }

    if (stickyBtn && form) {
      stickyBtn.addEventListener('click', function () {
        if (stickyBtn.disabled) return;
        /* Submit the real form so variant + selling_plan travel with it. */
        if (form.requestSubmit) {
          form.requestSubmit();
        } else {
          form.submit();
        }
      });
    }

    if (picker) {
      picker.addEventListener('pf:variant', function (e) {
        state.variant = e.detail.variant || null;
        render();
      });
    }

    root.addEventListener('change', function (e) {
      if (e.target.matches('[data-pf-qty-input]')) render();
    });

    /* ---- Gallery -------------------------------------------------------- */
    var mediaList = [];
    try {
      var listEl = root.querySelector('[data-pf-media-list]');
      if (listEl) mediaList = JSON.parse(listEl.textContent);
    } catch (e) {
      mediaList = [];
    }
    var thumbs = Array.prototype.slice.call(root.querySelectorAll('[data-pf-thumb]'));
    var thumbStrip = root.querySelector('[data-pf-thumbs]');
    var counter = root.querySelector('[data-pf-media-counter]');
    var stage = root.querySelector('[data-pf-stage]');
    var zoomBtn = root.querySelector('[data-pf-zoom]');
    var playBtn = root.querySelector('[data-pf-play]');
    var videoSlot = root.querySelector('[data-pf-video-slot]');
    /* The stage opens on the selected variant's photo, rendered server-side. */
    var mediaIndex = (stage && parseInt(stage.dataset.pfInitial, 10)) || 0;
    var suppressStageClick = false;

    var isVideo = function (i) {
      return !!mediaList[i] && /video/.test(mediaList[i].type || '');
    };

    /* A fresh player for media i, cloned from its template (null for photos). */
    var makeVideo = function (i) {
      var tpl = root.querySelector('template[data-pf-video="' + i + '"]');
      return tpl ? tpl.content.cloneNode(true) : null;
    };

    var stopStageVideo = function () {
      if (!videoSlot) return;
      videoSlot.innerHTML = ''; /* removing the element is what stops it */
      videoSlot.hidden = true;
      if (stage) stage.classList.remove('is-playing');
    };

    var playStageVideo = function () {
      var player = makeVideo(mediaIndex);
      if (!videoSlot || !player) return;
      videoSlot.innerHTML = '';
      videoSlot.appendChild(player);
      videoSlot.hidden = false;
      stage.classList.add('is-playing');
      var video = videoSlot.querySelector('video');
      if (video) {
        var playing = video.play();
        if (playing && playing.catch) playing.catch(function () {});
      }
    };

    /* Always centre the chosen thumb. Scrolling only when it was already out
       of view meant the thumbs at either edge - fully visible, so "in view" -
       never moved the strip, and the rest of a long gallery stayed hidden. */
    var centreIn = function (strip, item, smooth) {
      if (!strip || !item || strip.scrollWidth <= strip.clientWidth) return;
      var t = item.getBoundingClientRect();
      var s = strip.getBoundingClientRect();
      strip.scrollTo({
        left: strip.scrollLeft + (t.left - s.left) - (s.width - t.width) / 2,
        behavior: smooth === false ? 'auto' : 'smooth'
      });
    };
    var centreThumb = function (thumb, smooth) { centreIn(thumbStrip, thumb, smooth); };

    var showMedia = function (i, opts) {
      opts = opts || {};
      if (!mediaList.length || !mainImage) return;
      mediaIndex = (i + mediaList.length) % mediaList.length;
      var video = isVideo(mediaIndex);

      stopStageVideo();
      mainImage.src = mediaList[mediaIndex].full;
      mainImage.removeAttribute('srcset');
      if (stage) stage.classList.toggle('is-video', video);
      if (playBtn) playBtn.hidden = !video;
      if (zoomBtn) zoomBtn.hidden = video;

      thumbs.forEach(function (t, n) {
        t.setAttribute('aria-current', n === mediaIndex ? 'true' : 'false');
      });
      if (counter) counter.textContent = (mediaIndex + 1) + '/' + mediaList.length;
      centreThumb(thumbs[mediaIndex], opts.smooth);

      if (video && opts.play) playStageVideo();
    };

    var step = function (delta) { showMedia(mediaIndex + delta); };

    /* ---- Full-screen viewer --------------------------------------------- */
    var lb = root.querySelector('[data-pf-lightbox]');
    var openViewer = function () {};

    if (lb && typeof lb.showModal === 'function' && mediaList.length) {
      var lbStage = lb.querySelector('[data-pf-lb-stage]');
      var lbImg = lb.querySelector('[data-pf-lb-img]');
      var lbVideo = lb.querySelector('[data-pf-lb-video]');
      var lbCount = lb.querySelector('[data-pf-lb-count]');
      var lbIndex = 0;
      var lbStrip = lb.querySelector('[data-pf-lb-strip]');
      var lbThumbs = Array.prototype.slice.call(lb.querySelectorAll('[data-pf-lb-thumb]'));
      /* Set by a tap on the dark surround; the click that follows does the
         closing. Closing on the tap itself hands that click to whatever is
         underneath on the page - a ghost click that could reopen the viewer. */
      var closeOnClick = false;
      /* Zoom as scale + translation, with the image's transform-origin at its
         top-left corner, so "keep this point under the finger" is plain
         arithmetic. The image box fills the stage; object-fit does the rest. */
      var z = { s: 1, x: 0, y: 0 };
      var MAX_ZOOM = 4;
      var TAP_ZOOM = 2.5;

      var applyZoom = function (animate) {
        lbImg.style.transition = animate ? 'transform 0.25s ease' : 'none';
        lbImg.style.transform = 'translate(' + z.x + 'px,' + z.y + 'px) scale(' + z.s + ')';
        lb.classList.toggle('is-zoomed', z.s > 1.01);
      };

      /* Never let the picture be dragged off its own edges. */
      var clampPan = function () {
        var w = lbStage.clientWidth;
        var h = lbStage.clientHeight;
        z.x = Math.min(0, Math.max(w - w * z.s, z.x));
        z.y = Math.min(0, Math.max(h - h * z.s, z.y));
      };

      /* Zoom to scale s keeping stage point (px, py) where it is. */
      var zoomTo = function (s, px, py, animate) {
        s = Math.max(1, Math.min(MAX_ZOOM, s));
        z.x = px - (px - z.x) * (s / z.s);
        z.y = py - (py - z.y) * (s / z.s);
        z.s = s;
        if (s === 1) { z.x = 0; z.y = 0; }
        clampPan();
        applyZoom(animate);
      };

      var resetZoom = function (animate) {
        z = { s: 1, x: 0, y: 0 };
        applyZoom(animate);
      };

      var stopViewerVideo = function () {
        lbVideo.innerHTML = '';
        lbVideo.hidden = true;
      };

      var showInViewer = function (i) {
        stopViewerVideo();
        lbIndex = (i + mediaList.length) % mediaList.length;
        var m = mediaList[lbIndex];
        var shown = lbIndex;
        resetZoom(false);
        lbCount.textContent = (lbIndex + 1) + ' / ' + mediaList.length;
        lbThumbs.forEach(function (t, n) {
          t.setAttribute('aria-current', n === lbIndex ? 'true' : 'false');
        });
        if (lb.open) centreIn(lbStrip, lbThumbs[lbIndex]);

        if (isVideo(lbIndex)) {
          lb.classList.add('is-video');
          lbImg.hidden = true;
          var player = makeVideo(lbIndex);
          if (player) {
            lbVideo.appendChild(player);
            lbVideo.hidden = false;
          }
        } else {
          lb.classList.remove('is-video');
          lbImg.hidden = false;
          lbImg.alt = m.alt || '';
          /* The 1300px copy is usually cached already, so show it at once and
             swap in the 2400px one for zooming when it arrives. */
          lbImg.src = m.full;
          var sharp = new Image();
          sharp.onload = function () { if (lbIndex === shown) lbImg.src = m.zoom; };
          sharp.src = m.zoom;
        }

        /* Warm the neighbours so the next swipe is instant. */
        [lbIndex + 1, lbIndex - 1].forEach(function (n) {
          var k = (n + mediaList.length) % mediaList.length;
          if (k !== lbIndex && !isVideo(k)) {
            var warm = new Image();
            warm.src = mediaList[k].zoom;
          }
        });
      };

      openViewer = function (i) {
        stopStageVideo();
        showInViewer(i);
        document.documentElement.classList.add('pf-lb-open');
        lb.showModal();
        /* Only measurable once the dialog is showing. */
        centreIn(lbStrip, lbThumbs[lbIndex], false);
      };

      lb.addEventListener('close', function () {
        stopViewerVideo();
        document.documentElement.classList.remove('pf-lb-open');
        /* Come back to wherever the viewer was left. */
        showMedia(lbIndex, { smooth: false });
      });

      lb.addEventListener('click', function (e) {
        if (closeOnClick) { closeOnClick = false; lb.close(); return; }
        if (e.target.closest('[data-pf-lb-close]')) { lb.close(); return; }
        if (e.target.closest('[data-pf-lb-prev]')) { showInViewer(lbIndex - 1); return; }
        if (e.target.closest('[data-pf-lb-next]')) { showInViewer(lbIndex + 1); return; }
        var lt = e.target.closest('[data-pf-lb-thumb]');
        if (lt) { showInViewer(parseInt(lt.dataset.index, 10)); return; }
        /* The dark surround closes it: the dialog's own background and the top
           bar. The strip is left alone, so a near-miss on a thumb doesn't. */
        if (e.target === lb || e.target.classList.contains('pf-lightbox__bar')) { lb.close(); return; }
        /* Around a video too - the player itself keeps its own clicks. */
        if (lb.classList.contains('is-video') && e.target.closest('[data-pf-lb-stage]') && !e.target.closest('video, iframe')) {
          lb.close();
        }
      });

      lb.addEventListener('keydown', function (e) {
        if (e.key === 'ArrowLeft') { e.preventDefault(); showInViewer(lbIndex - 1); }
        else if (e.key === 'ArrowRight') { e.preventDefault(); showInViewer(lbIndex + 1); }
      });

      /* One set of pointer handlers for mouse, pen and touch: a tap toggles
         zoom at that spot, two fingers pinch, a drag pans once zoomed and
         otherwise swipes to the next or previous photo. */
      var pointers = new Map();
      var g = null;
      var local = function (p) {
        var r = lbStage.getBoundingClientRect();
        return { x: p.x - r.left, y: p.y - r.top };
      };
      var pair = function () {
        var pts = [];
        pointers.forEach(function (p) { pts.push(p); });
        return pts;
      };

      /* Where the photo actually sits: its box fills the stage and
         object-fit: contain centres the picture inside it. */
      var onPicture = function (x, y) {
        var nw = lbImg.naturalWidth;
        var nh = lbImg.naturalHeight;
        if (!nw || !nh) return true;
        var box = lbStage.getBoundingClientRect();
        var k = Math.min(box.width / nw, box.height / nh);
        var left = box.left + (box.width - nw * k) / 2;
        var top = box.top + (box.height - nh * k) / 2;
        return x >= left - 2 && x <= left + nw * k + 2 && y >= top - 2 && y <= top + nh * k + 2;
      };

      lbStage.addEventListener('pointerdown', function (e) {
        closeOnClick = false;
        if (lb.classList.contains('is-video')) return;
        if (e.pointerType === 'mouse' && e.button !== 0) return;
        lbStage.setPointerCapture(e.pointerId);
        pointers.set(e.pointerId, { x: e.clientX, y: e.clientY });
        if (pointers.size === 1) {
          g = { mode: 'tap', x0: e.clientX, y0: e.clientY, zx: z.x, zy: z.y };
        } else if (pointers.size === 2) {
          var pts = pair();
          var mid = local({ x: (pts[0].x + pts[1].x) / 2, y: (pts[0].y + pts[1].y) / 2 });
          g = { mode: 'pinch', d0: Math.hypot(pts[0].x - pts[1].x, pts[0].y - pts[1].y) || 1, s0: z.s, mid: mid };
        }
      });

      lbStage.addEventListener('pointermove', function (e) {
        if (!g || !pointers.has(e.pointerId)) return;
        pointers.set(e.pointerId, { x: e.clientX, y: e.clientY });

        if (g.mode === 'pinch' && pointers.size === 2) {
          var pts = pair();
          var d = Math.hypot(pts[0].x - pts[1].x, pts[0].y - pts[1].y);
          var mid = local({ x: (pts[0].x + pts[1].x) / 2, y: (pts[0].y + pts[1].y) / 2 });
          zoomTo(g.s0 * d / g.d0, mid.x, mid.y, false);
          return;
        }
        if (pointers.size !== 1) return;

        var dx = e.clientX - g.x0;
        var dy = e.clientY - g.y0;
        if (g.mode === 'tap' && Math.hypot(dx, dy) > 8) g.mode = z.s > 1.01 ? 'pan' : 'swipe';
        if (g.mode === 'pan') {
          z.x = g.zx + dx;
          z.y = g.zy + dy;
          clampPan();
          applyZoom(false);
        } else if (g.mode === 'swipe') {
          /* The photo follows the finger, so the gesture reads as a swipe. */
          lbImg.style.transition = 'none';
          lbImg.style.transform = 'translate(' + dx + 'px,0)';
        }
      });

      var endPointer = function (e) {
        if (!pointers.has(e.pointerId)) return;
        pointers.delete(e.pointerId);
        if (!g) return;

        if (g.mode === 'pinch') {
          if (pointers.size === 1) {
            /* One finger lifted: carry on as a pan from here. */
            var rest = pair()[0];
            g = { mode: 'pan', x0: rest.x, y0: rest.y, zx: z.x, zy: z.y };
          } else if (pointers.size === 0) {
            if (z.s < 1.05) resetZoom(true);
            g = null;
          }
          return;
        }
        if (pointers.size > 0) return;

        if (g.mode === 'tap' && e.type === 'pointerup') {
          var p = local({ x: e.clientX, y: e.clientY });
          if (z.s > 1.01) resetZoom(true);
          else if (!onPicture(e.clientX, e.clientY)) closeOnClick = true;
          else zoomTo(TAP_ZOOM, p.x, p.y, true);
        } else if (g.mode === 'swipe') {
          var dx = e.clientX - g.x0;
          if (Math.abs(dx) > 60 && mediaList.length > 1) showInViewer(lbIndex + (dx < 0 ? 1 : -1));
          else resetZoom(true);
        }
        g = null;
      };
      lbStage.addEventListener('pointerup', endPointer);
      lbStage.addEventListener('pointercancel', endPointer);

      /* Trackpad pinch arrives as ctrl + wheel; two-finger scroll pans once
         zoomed in. Plain wheel on an unzoomed photo is left alone. */
      lbStage.addEventListener('wheel', function (e) {
        if (lb.classList.contains('is-video')) return;
        if (!e.ctrlKey && z.s <= 1.01) return;
        e.preventDefault();
        if (e.ctrlKey) {
          var p = local({ x: e.clientX, y: e.clientY });
          zoomTo(z.s * Math.exp(-e.deltaY * 0.01), p.x, p.y, false);
        } else {
          z.x -= e.deltaX;
          z.y -= e.deltaY;
          clampPan();
          applyZoom(false);
        }
      }, { passive: false });

      window.addEventListener('resize', function () {
        if (lb.open && z.s > 1.01) { clampPan(); applyZoom(false); }
      });
    }

    /* Swipe the main photo on touch screens. Passive, and only a clearly
       sideways movement counts, so the page still scrolls normally. */
    if (stage && mediaList.length > 1) {
      var sx = null;
      var sy = null;
      stage.addEventListener('touchstart', function (e) {
        if (stage.classList.contains('is-playing')) { sx = null; return; }
        sx = e.touches[0].clientX;
        sy = e.touches[0].clientY;
      }, { passive: true });
      stage.addEventListener('touchend', function (e) {
        if (sx === null) return;
        var dx = e.changedTouches[0].clientX - sx;
        var dy = e.changedTouches[0].clientY - sy;
        sx = null;
        if (Math.abs(dx) > 40 && Math.abs(dx) > Math.abs(dy) * 1.5) {
          step(dx < 0 ? 1 : -1);
          suppressStageClick = true;
          setTimeout(function () { suppressStageClick = false; }, 400);
        }
      }, { passive: true });
    }

    root.addEventListener('click', function (e) {
      /* The subscription app rewrites its prices after a click; re-mirror once
         it has settled. */
      if (e.target.closest('.subify-root')) setTimeout(renderSticky, 260);

      var down = e.target.closest('[data-pf-qty-down]');
      var up = e.target.closest('[data-pf-qty-up]');
      if ((down || up) && qtyInput) {
        e.preventDefault();
        var next = qty() + (up ? 1 : -1);
        qtyInput.value = next < 1 ? 1 : next;
        render();
        return;
      }

      var thumb = e.target.closest('[data-pf-thumb]');
      if (thumb) {
        e.preventDefault();
        var ti = parseInt(thumb.dataset.index, 10);
        /* Choosing the video's own thumbnail is asking to watch it. */
        showMedia(ti, { play: isVideo(ti) });
        return;
      }

      if (e.target.closest('[data-pf-media-prev]')) { e.preventDefault(); step(-1); return; }
      if (e.target.closest('[data-pf-media-next]')) { e.preventDefault(); step(1); return; }
      if (e.target.closest('[data-pf-play]')) { e.preventDefault(); playStageVideo(); return; }
      if (e.target.closest('[data-pf-zoom]')) { e.preventDefault(); openViewer(mediaIndex); return; }

      if (e.target.closest('[data-pf-main-image]') && !suppressStageClick) {
        if (isVideo(mediaIndex)) playStageVideo();
        else openViewer(mediaIndex);
      }
    });

    /* Bring the current thumb into the middle of the strip on arrival. */
    centreThumb(thumbs[mediaIndex], false);

    /* Arrow keys while the gallery has focus, the way a lightbox behaves. */
    var gallery = root.querySelector('[data-pf-gallery]');
    if (gallery) {
      gallery.addEventListener('keydown', function (e) {
        if (e.key === 'ArrowLeft') { e.preventDefault(); step(-1); }
        else if (e.key === 'ArrowRight') { e.preventDefault(); step(1); }
      });
    }

    render();

    /* App widgets inject after load; re-check once they have had a chance to. */
    setTimeout(render, 1200);
  };

  var boot = function () {
    Array.prototype.forEach.call(document.querySelectorAll('.pf-pdp'), initRoot);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  /* Theme editor re-renders sections without a full page load. */
  document.addEventListener('shopify:section:load', boot);
})();
